<script lang="ts" setup>
import { useKeenSlider } from 'keen-slider/vue'
import 'keen-slider/keen-slider.min.css'

const props = defineProps<{
  specs: {
    id: number
    pool_tech_spec_id: {
      id: number
      name: string
      image: string
      length: string
      depth: string
      width: string
      volume: string
      translations: DirectusTranslation<{
        safety_rim: string
        standing_ledge: string
        roldeck_integration: string
        stairs: string
      }>
    }
  }[]
  data: {
    id: number
    translations: DirectusTranslation<{
      title: string
      section_title: string
    }>
  }
}>()

const { t } = useI18n()
const { dt } = useDirectusTranslation()
const { hyphen } = useHyphen()

const specs = computed(() => {
  return props.specs.map((item) => {
    return {
      ...item.pool_tech_spec_id,
      ...dt(item.pool_tech_spec_id.translations),
      translations: undefined,
    }
  })
})

const activeSlide = ref(0)

const [container, slider] = useKeenSlider({
  loop: false,
  renderMode: 'performance',
  drag: true,
  slides: {
    perView: 1,
  },
  slideChanged: (s) => {
    // console.log('slide changed', s.track.details.rel)
    activeSlide.value = s.track.details.rel
  },
})

function nextSlide() {
  slider?.value?.next()
}
function prevSlide() {
  slider?.value?.prev()
}

slider.value?.moveToIdx(0)

const activeSpecIndex = computed({
  get: () => activeSlide.value,
  set: (value) => {
    if (value !== activeSlide.value)
      slider?.value?.moveToIdx(value)
  },
})

const activeSpecIndexString = computed({
  get: () => activeSlide.value.toString(),
  set: (value) => {
    activeSpecIndex.value = Number.parseInt(value)
  },

})

const activeSpec = computed(() => {
  return specs.value[activeSpecIndex.value]
})
</script>

<template>
  <BlockBase is-light class="bg-neutral-white py-15rem text-neutral-black signature-page:bg-signature-darkGray signature-page:text-neutral-white">
    <Container class="grid grid-cols-1 w-full gap-10rem lg:grid-cols-2">
      <!-- SLIDER -->
      <div
        ref="container" class="keen-slider my-auto h-65rem w-full"
      >
        <div v-for="spec, index in specs" :key="index" class="keen-slider__slide size-full">
          <NuxtImg
            loading="lazy"
            provider="directus"
            :src="spec.image"
            :alt="spec.name"
            class="h-full w-full object-cover"
            height="650"
            width="650"
          />
        </div>

        <div v-if="specs.length > 1" class="absolute bottom-0 right-0 h-10rem w-15rem flex items-center justify-center bg-neutral-white signature-page:bg-signature-black signature-page:text-signature">
          <button class="h-10rem w-7.5rem flex items-center justify-center transition hover:bg-starline-secondary/10 signature-page:hover:bg-neutral-white/10" @click="prevSlide">
            <IconSmallArrow class="h-2rem w-2rem" />
          </button>
          <button class="h-10rem w-7.5rem flex items-center justify-center transition hover:bg-starline-secondary/10 signature-page:hover:bg-neutral-white/10" @click="nextSlide">
            <IconSmallArrow class="h-2rem w-2rem rotate-180" />
          </button>
        </div>
      </div>

      <!-- Content -->
      <div
        class="my-auto w-full"
      >
        <h1 class="mb-4rem text-balance text-4rem font-title md:text-7.6rem">
          {{ hyphen(dt(props.data.translations)?.title) }}
        </h1>
        <TextWithAfterLine v-if="dt(props.data.translations)?.section_title" class="mb-2rem" :text="dt(props.data.translations)?.section_title ?? ''" />
        <div class="mt-8rem">
          <RadixRadioGroupRoot
            v-model="activeSpecIndexString"
          >
            <div class="w-full flex flex-wrap gap-2rem">
              <RadixRadioGroupItem
                v-for="item, index in specs" :key="index" :value="index.toString()"
                class="inline-block cursor-pointer select-none bg-starline-secondary px-1.6rem py-.8rem text-1.3rem text-neutral-white tracking-[.4rem] uppercase shadow-none ring-1 ring-neutral-darkGray transition duration-300ms signature-page:bg-signature-black data-[active=true]:shadow-starline data-[active=true]:ring-3 data-[active=true]:ring-starline-primary data-[active=true]:signature-page:shadow-signature data-[active=true]:signature-page:ring-signature hover:ring-starline-primary signature-page:ring-signature/14 hover:signature-page:ring-signature"
              >
                {{ item.name }}
                <!-- <RadixRadioGroupIndicator>indicator</RadixRadioGroupIndicator> -->
              </RadixRadioGroupItem>
            </div>
          </RadixRadioGroupRoot>
        </div>
        <div class="grid grid-cols-2 mt-8rem w-full gap-2rem">
          <div>
            <h2 class="text-1.6rem">
              {{ t('pool.spec.length') }}
            </h2>
            <p class="mt-1rem text-1.1rem text-neutral-gray tracking-[.3rem] uppercase">
              {{ activeSpec?.length }}
            </p>
          </div>
          <div>
            <h2 class="text-1.6rem">
              {{ t('pool.spec.depth') }}
            </h2>
            <p class="mt-1rem text-1.1rem text-neutral-gray tracking-[.3rem] uppercase">
              {{ activeSpec?.depth }}
            </p>
          </div>
          <div>
            <h2 class="text-1.6rem">
              {{ t('pool.spec.width') }}
            </h2>
            <p class="mt-1rem text-1.1rem text-neutral-gray tracking-[.3rem] uppercase">
              {{ activeSpec?.width }}
            </p>
          </div>
          <div>
            <h2 class="text-1.6rem">
              {{ t('pool.spec.volume') }}
            </h2>
            <p class="mt-1rem text-1.1rem text-neutral-gray tracking-[.3rem] uppercase">
              {{ activeSpec?.volume }}
            </p>
          </div>
          <div>
            <h2 class="text-1.6rem">
              {{ t('pool.spec.safety_rim') }}
            </h2>
            <p class="mt-1rem text-1.1rem text-neutral-gray tracking-[.3rem] uppercase">
              {{ activeSpec?.safety_rim }}
            </p>
          </div>
          <div>
            <h2 class="text-1.6rem">
              {{ t('pool.spec.standing_legde') }}
            </h2>
            <p class="mt-1rem text-1.1rem text-neutral-gray tracking-[.3rem] uppercase">
              {{ activeSpec?.standing_ledge }}
            </p>
          </div>
          <div>
            <h2 class="text-1.6rem">
              {{ t('pool.spec.roldeck_integration') }}
            </h2>
            <p class="mt-1rem text-1.1rem text-neutral-gray tracking-[.3rem] uppercase">
              {{ activeSpec?.roldeck_integration }}
            </p>
          </div>
          <div>
            <h2 class="text-1.6rem">
              {{ t('pool.spec.stairs') }}
            </h2>
            <p class="mt-1rem text-1.1rem text-neutral-gray tracking-[.3rem] uppercase">
              {{ activeSpec?.stairs }}
            </p>
          </div>
        </div>
      </div>
    </Container>
  </BlockBase>
</template>

<style>

</style>
